import { FC, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';

import Helmet from '@/components/meta/Helmet';
import ErrorTemplate from '@/components/templates/Error';
import SingleraceResultTemplate from '@/components/templates/Velodrome/Result/Singlerace';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import {
  RaceProgram,
  useGetRaceProgramsLazyQuery,
  useGetSingleraceResultQuery,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { VelodromeRoutesProps } from '@/routes/Velodrome';
import { toHalfWidth } from '@/utility/utils';

const SingleraceResultPage: FC<VelodromeRoutesProps> = ({ velodrome }) => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const { heldDate } = useParams<{
    heldDate: string;
  }>();

  const singleraceResultQuery = useGetSingleraceResultQuery({
    variables: {
      input: {
        velodromeCode: velodrome.code,
        heldDate: heldDate ? dayjst(heldDate) : null,
      },
    },
    fetchPolicy: 'network-only',
  });

  const [loadRacePrograms, raceProgramsQuery] = useGetRaceProgramsLazyQuery();

  useEffect(() => {
    if (!singleraceResultQuery.data) {
      return;
    }
    if (!raceProgramsQuery.data && !raceProgramsQuery.loading) {
      loadRacePrograms({
        variables: {
          input: {
            velodromeCode: velodrome.code,
            heldFirstDate: singleraceResultQuery.data.raceProgram.heldFirstDate,
          },
        },
      });
    }
    setBreadcrumbs([
      {
        name: `${velodrome.name}競輪`,
        to: `/${velodrome.romanName}`,
      },
      {
        name: toHalfWidth(
          `${
            singleraceResultQuery.data.raceProgram.programName ||
            `${velodrome.name}競輪 ${singleraceResultQuery.data.raceProgram.gradeName}`
          }（${singleraceResultQuery.data.raceProgram.heldDate.format(
            'YYYY年M月D日',
          )}）`,
        ),
        to: '',
      },
    ]);
  }, [singleraceResultQuery.data, raceProgramsQuery.data]);

  const title = `${velodrome.name}競輪 ${toHalfWidth(
    singleraceResultQuery.data
      ? singleraceResultQuery.data.raceProgram.programName
      : '',
  )}（${dayjst(heldDate).format('YYYY年M月D日')}）レース結果`;

  const description = `${velodrome.name}競輪 ${toHalfWidth(
    singleraceResultQuery.data
      ? singleraceResultQuery.data.raceProgram.programName
      : '',
  )}の全レース結果です。`;

  return (
    <>
      <Helmet
        meta={{
          title,
          description: `${description}%s`,
          keywords: `${title},%s`,
        }}
      />
      {singleraceResultQuery.error || raceProgramsQuery.error ? (
        <ErrorTemplate
          apolloError={singleraceResultQuery.error || raceProgramsQuery.error}
        />
      ) : (
        <>
          {singleraceResultQuery.data && raceProgramsQuery.data && (
            <SingleraceResultTemplate
              raceProgram={
                singleraceResultQuery.data.raceProgram as RaceProgram
              }
              racePrograms={
                raceProgramsQuery.data.racePrograms as RaceProgram[]
              }
            />
          )}
        </>
      )}
    </>
  );
};
export default SingleraceResultPage;
